'use client';

import dayjs from 'dayjs';

import { Text, Heading } from '@/components';
import { InStoreOrderDetail, OrderDetailProps } from '@/models/order';
import { EMAIL_ID_SUPPORT, LANDLINE_NUMBER } from '@/constants';
import Link from 'next/link';
import { useMemo } from 'react';

type DeliveryPickupAddressPropType = {
  details?: (OrderDetailProps & InStoreOrderDetail) | any | undefined;
  isEvent: boolean;
};

const DeliveryPickupAddress = ({
  details,
  isEvent,
}: DeliveryPickupAddressPropType) => {
  const { shipping_method_code, shipping_data } = details || {};
  const isPickup =
    shipping_method_code === 'freeshipping_freeshipping' && !isEvent;

  const deliveryDateTime = useMemo(() => {
    if (!details?.delivery_date?.includes('to')) {
      return dayjs(details?.delivery_date)
        .add(3, 'hours')
        .format('DD MMM YYYY, hh:mm A');
    } else {
      const deliveryDate = details?.delivery_date;
      if (deliveryDate) {
        const [start, end] = deliveryDate.split(' to ');
        const date = start.split(' ')[0];
        const fullEndTime = `${date} ${end}`;
        const startTime = dayjs(start).format('YYYY-MM-DD, h:mm A');
        const endTime = dayjs(fullEndTime).format('h:mm A');
        return `${startTime} to ${endTime}`;
      }
    }
  }, [details]);

  return (
    <div aria-label='delivery-pickup-address' className='space-y-4'>
      <Heading as='h4' className='mb-4 text-base font-bold'>
        {!shipping_method_code ? 'Store' : isPickup ? 'Pickup' : 'Delivery'}{' '}
        Address
      </Heading>
      <div aria-label='address'>
        {shipping_method_code !== 'freeshipping_freeshipping' && (
          <Text className='text-sm font-bold'>
            {details?.shipping_data?.firstname}{' '}
            {details?.shipping_data?.lastname}
          </Text>
        )}
        <Text className='mt-2 text-sm'>
          {!shipping_method_code
            ? `${shipping_data?.street_line1}${shipping_data?.street_line2}, ${shipping_data?.city}`
            : shipping_method_code === 'freeshipping_freeshipping'
              ? `${shipping_data?.street1}, ${shipping_data?.street2}, ${shipping_data?.region}`
              : `${shipping_data?.street1}${
                  shipping_data?.street2 ? `, ${shipping_data?.street2}` : ''
                }, ${shipping_data?.city}, ${shipping_data?.region}`}
        </Text>
        {shipping_method_code !== 'freeshipping_freeshipping' ? (
          <ul className='mt-3 flex flex-col gap-x-4 px-3 md:flex-row md:items-center'>
            <li>
              <Link
                href={`tel:${
                  !shipping_method_code
                    ? shipping_data?.telephone
                    : shipping_data?.mobile
                }`}
                className='text-sm font-semibold'>
                {!shipping_method_code
                  ? shipping_data?.telephone
                  : shipping_data?.mobile}
              </Link>
            </li>
            <li>
              <Link
                href={`mailto:${shipping_data?.email}`}
                className='text-sm font-semibold'>
                {shipping_data?.email}
              </Link>
            </li>
          </ul>
        ) : (
          <ul className='mt-3 flex flex-col gap-x-4 px-3 md:flex-row md:items-center'>
            <li>
              <Link
                href={`tel:${LANDLINE_NUMBER}`}
                className='text-sm font-semibold'>
                {LANDLINE_NUMBER}
              </Link>
            </li>
            <li>
              <Link
                href={`mailto:${EMAIL_ID_SUPPORT}`}
                className='text-sm font-semibold'>
                {EMAIL_ID_SUPPORT}
              </Link>
            </li>
          </ul>
        )}
      </div>

      <div aria-label='date-time'>
        <Text className='text-sm font-bold'>Date & Time</Text>
        <Text className='mt-2 text-sm'>{deliveryDateTime}</Text>
      </div>
    </div>
  );
};

export default DeliveryPickupAddress;
